import { RouterAwareLink } from '../../../../core'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import {
  useRouterOrgSlugFor,
  useSafeRouterOrgSlug,
} from '../../../utils/orgSlug'
import { serializeEncounterNavigationSource } from '../hooks/useEncounterNavigationSource'
import type { EncounterNavigationSource } from '../hooks/useEncounterNavigationSource'
import { serializeEncounterRouteQueryParams } from '../utils/queryParams'
import type { EncounterRouteQueryParams } from '../utils/queryParams'
import { getWorkspaceEncounterHref } from '../utils/workspaceEncounterHrefs'
import type { LinkProps } from 'react-router-dom'
import type { Nullable } from 'nuqs'
export interface EncounterLinkProps extends Omit<LinkProps, 'to'> {
  encounterId: string
  navigationSource?: EncounterNavigationSource
  orgSlug?: string | null
  queryParams?: Partial<Nullable<EncounterRouteQueryParams>> | null
}

export function EncounterLink({
  children,
  encounterId,
  navigationSource,
  orgSlug,
  queryParams = null,
  rel,
  target,
  ...restProps
}: EncounterLinkProps) {
  const currentRouterOrgSlug = useSafeRouterOrgSlug()
  const providedRouterOrgSlug = useRouterOrgSlugFor(orgSlug ?? null)
  const canViewEncounter = useHasUserCapability('encounter.details.view')
  const resolvedOrgSlug =
    orgSlug === undefined ? currentRouterOrgSlug : providedRouterOrgSlug

  if (!canViewEncounter || resolvedOrgSlug === null) {
    return <span {...restProps}>{children}</span>
  }

  const workspaceEncounterHref = getWorkspaceEncounterHref({
    encounterId,
    orgSlug: resolvedOrgSlug,
  })
  const baseUrl = serializeEncounterRouteQueryParams(
    workspaceEncounterHref,
    queryParams,
  )
  const to = navigationSource
    ? serializeEncounterNavigationSource(baseUrl, navigationSource)
    : baseUrl

  return (
    <RouterAwareLink to={to} target={target} rel={rel} {...restProps}>
      {children}
    </RouterAwareLink>
  )
}
